import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  User, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Shield, 
  UserCheck, 
  UserX,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Filter,
  Upload,
  AlertTriangle,
  Download,
  FileDown,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Copy,
  Key,
  Image,
  X
} from 'lucide-react';

// Composant MembersList en dehors pour éviter la perte de focus
const MembersList = ({ members, searchTerm, setSearchTerm, title, emptyMessage, onView, onEdit, onDelete, getRoleBadgeColor, getRoleLabel }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
          {title} ({members.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Barre de recherche spécifique */}
        <div className="mb-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <Input
              placeholder="Rechercher par nom ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Liste des membres */}
        {members.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {emptyMessage}
          </div>
        ) : (
          <div className="space-y-4">
            {members.map((member) => (
              <div key={member.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="space-y-3">
                  {/* Nom avec statut */}
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {member.profile_picture ? (
                        <img 
                          src={member.profile_picture.startsWith('http') ? member.profile_picture : `${process.env.REACT_APP_BACKEND_URL}${member.profile_picture}`} 
                          alt={`${member.first_name} ${member.last_name}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>';
                          }}
                        />
                      ) : (
                        <User className="w-6 h-6 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg flex items-center gap-2 flex-wrap">
                        {member.first_name} {member.last_name}
                        {member.is_moderator && (
                          <Shield className="w-4 h-4 text-blue-500" />
                        )}
                        {member.has_missing_data && (
                          <AlertCircle className="w-4 h-4 text-red-500" title="Compte incomplet - données manquantes" />
                        )}
                        {member.has_missing_email && !member.has_missing_data && (
                          <AlertTriangle className="w-4 h-4 text-orange-500" title="Email manquant" />
                        )}
                        {!member.has_missing_data && !member.has_missing_email && member.email && (
                          <CheckCircle className="w-4 h-4 text-green-500" title="Compte complet" />
                        )}
                      </h3>
                      
                      {/* Email */}
                      {member.email && (
                        <div className="flex items-center text-sm text-gray-600 mt-1">
                          <Mail className="w-4 h-4 mr-2" />
                          {member.email}
                        </div>
                      )}
                      {member.has_missing_email && (
                        <span className="text-orange-500 text-sm">(Email manquant)</span>
                      )}
                      
                      {/* Téléphone et Ville */}
                      <div className="flex items-center flex-wrap gap-3 text-sm text-gray-500 mt-2">
                        {member.phone && (
                          <div className="flex items-center">
                            <Phone className="w-4 h-4 mr-1" />
                            {member.phone}
                          </div>
                        )}
                        {member.city && (
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {member.city}
                          </div>
                        )}
                        {member.membership_date && (
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(member.membership_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      
                      {/* Tags (Badges) */}
                      <div className="flex items-center flex-wrap gap-2 mt-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
                          {getRoleLabel(member.role)}
                        </span>
                        {member.language && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {member.language}
                          </span>
                        )}
                        {member.civilite && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {member.civilite}
                          </span>
                        )}
                        {member.has_missing_data && (
                          <span className="text-red-500 text-xs">(Données incomplètes)</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2 md:space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onView(member)}
                      className="flex-1 md:flex-none"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Voir
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onEdit(member)}
                      className="flex-1 md:flex-none"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Modifier
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const printWindow = window.open('', '_blank', 'width=800,height=600');
                        printWindow.document.write(`
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <title>Fiche Membre - ${member.first_name} ${member.last_name}</title>
                            <style>
                              body {
                                font-family: Arial, sans-serif;
                                padding: 40px;
                                color: #333;
                              }
                              .header {
                                text-align: center;
                                margin-bottom: 30px;
                                border-bottom: 3px solid rgb(196, 150, 96);
                                padding-bottom: 20px;
                              }
                              .logo {
                                max-width: 100px;
                                margin-bottom: 10px;
                              }
                              h1 {
                                color: rgb(196, 150, 96);
                                font-size: 28px;
                                margin: 10px 0;
                              }
                              h2 {
                                color: rgb(196, 150, 96);
                                font-size: 20px;
                                margin-top: 30px;
                                border-bottom: 2px solid rgb(196, 150, 96);
                                padding-bottom: 5px;
                              }
                              .info-grid {
                                display: grid;
                                grid-template-columns: 200px 1fr;
                                gap: 15px;
                                margin-bottom: 20px;
                              }
                              .info-label {
                                font-weight: bold;
                                color: #666;
                              }
                              .info-value {
                                color: #333;
                              }
                              .badge {
                                display: inline-block;
                                padding: 4px 12px;
                                border-radius: 12px;
                                font-size: 12px;
                                font-weight: bold;
                                margin-right: 8px;
                              }
                              .badge-role {
                                background-color: #e0f2fe;
                                color: #0369a1;
                              }
                              .badge-admin {
                                background-color: #fee2e2;
                                color: #991b1b;
                              }
                              .footer {
                                margin-top: 40px;
                                text-align: center;
                                font-size: 12px;
                                color: #999;
                              }
                              @media print {
                                body {
                                  padding: 20px;
                                }
                              }
                            </style>
                          </head>
                          <body>
                            <div class="header">
                              <h1>Fiche Membre</h1>
                              <p style="color: #666; font-size: 14px;">Les Lignages de Bruxelles</p>
                            </div>

                            <h2>Informations personnelles</h2>
                            <div class="info-grid">
                              <div class="info-label">Civilité:</div>
                              <div class="info-value">${member.civilite || 'NC'}</div>
                              
                              <div class="info-label">Prénom:</div>
                              <div class="info-value">${member.first_name || 'NC'}</div>
                              
                              <div class="info-label">Nom:</div>
                              <div class="info-value">${member.last_name || 'NC'}</div>
                              
                              <div class="info-label">Email:</div>
                              <div class="info-value">${member.email || 'NC'}</div>
                              
                              <div class="info-label">Téléphone:</div>
                              <div class="info-value">${member.phone || 'NC'}</div>
                              
                              <div class="info-label">GSM:</div>
                              <div class="info-value">${member.gsm || 'NC'}</div>
                              
                              <div class="info-label">Date de naissance:</div>
                              <div class="info-value">${member.date_naissance || 'NC'}</div>
                            </div>

                            <h2>Adresse</h2>
                            <div class="info-grid">
                              <div class="info-label">Adresse:</div>
                              <div class="info-value">${member.address || 'NC'}</div>
                              
                              <div class="info-label">Code postal:</div>
                              <div class="info-value">${member.postal_code || 'NC'}</div>
                              
                              <div class="info-label">Ville:</div>
                              <div class="info-value">${member.city || 'NC'}</div>
                              
                              <div class="info-label">Pays:</div>
                              <div class="info-value">${member.country || 'NC'}</div>
                            </div>

                            <h2>Informations d'adhésion</h2>
                            <div class="info-grid">
                              <div class="info-label">Type de membre:</div>
                              <div class="info-value">
                                <span class="badge badge-role">${getRoleLabel(member.role)}</span>
                                ${member.role === 'admin' ? '<span class="badge badge-admin">Admin</span>' : ''}
                              </div>
                              
                              <div class="info-label">Type d'adhésion:</div>
                              <div class="info-value">${member.membership_type || 'NC'}</div>
                              
                              <div class="info-label">Date d'adhésion:</div>
                              <div class="info-value">${member.membership_date ? new Date(member.membership_date).toLocaleDateString('fr-FR') : 'NC'}</div>
                              
                              <div class="info-label">Langue:</div>
                              <div class="info-value">${member.language || 'NC'}</div>
                              
                              <div class="info-label">Lignage:</div>
                              <div class="info-value">${member.lignage || 'NC'}</div>
                              
                              <div class="info-label">Consentement RGPD:</div>
                              <div class="info-value">${member.rgpd_consent ? 'Oui' : 'Non'}</div>
                            </div>

                            ${member.comments ? `
                              <h2>Commentaires</h2>
                              <div style="padding: 15px; background-color: #f9fafb; border-radius: 8px;">
                                ${member.comments}
                              </div>
                            ` : ''}

                            ${member.admin_notes ? `
                              <h2>Notes administrateur</h2>
                              <div style="padding: 15px; background-color: #fff7ed; border-radius: 8px; border-left: 4px solid rgb(196, 150, 96);">
                                ${member.admin_notes}
                              </div>
                            ` : ''}

                            <div class="footer">
                              <p>Imprimé le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
                              <p>Les Lignages de Bruxelles - Document confidentiel</p>
                            </div>

                            <script>
                              window.onload = function() {
                                window.print();
                              };
                              
                              window.onafterprint = function() {
                                window.close();
                              };
                              
                              // Pour Safari qui ne supporte pas onafterprint
                              if (window.matchMedia) {
                                const mediaQueryList = window.matchMedia('print');
                                mediaQueryList.addListener(function(mql) {
                                  if (!mql.matches) {
                                    window.close();
                                  }
                                });
                              }
                            </script>
                          </body>
                          </html>
                        `);
                        printWindow.document.close();
                      }}
                      style={{ color: 'rgb(196, 150, 96)', borderColor: 'rgb(196, 150, 96)' }}
                      className="flex-1 md:flex-none"
                    >
                      <FileDown className="w-4 h-4 mr-1" />
                      Imprimer
                    </Button>
                    {member.role !== 'admin' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onDelete(member.id)}
                        className="text-red-600 hover:text-red-700 flex-1 md:flex-none"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MembersManagement = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('view'); // 'view', 'edit', 'create'
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // États pour les onglets de filtrage
  const [activeTab, setActiveTab] = useState('complets');
  const [searchTermComplets, setSearchTermComplets] = useState('');
  const [searchTermManquants, setSearchTermManquants] = useState('');
  const [searchTermIncomplets, setSearchTermIncomplets] = useState('');

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    filterMembers();
  }, [members, searchTerm, filterRole]);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      } else {
        console.error('Erreur lors de la récupération des membres');
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterMembers = () => {
    let filtered = members;

    // Filtrer par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter(member => 
        member.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrer par rôle
    if (filterRole !== 'all') {
      filtered = filtered.filter(member => member.role === filterRole);
    }

    setFilteredMembers(filtered);
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Administrateur';
      case 'membre_vie': return 'Membre à vie';
      case 'membre_annuel': return 'Membre annuel';
      case 'membre_exterieur': return 'Membre extérieur';
      case 'decede': return 'Décédé';
      case 'prospect': return 'Prospect';
      case 'non_actif': return 'Non-actif';
      default: return role;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'membre_vie': return 'bg-green-100 text-green-800';
      case 'membre_annuel': return 'bg-blue-100 text-blue-800';
      case 'membre_exterieur': return 'bg-gray-100 text-gray-800';
      case 'decede': return 'bg-slate-200 text-slate-700';
      case 'prospect': return 'bg-amber-100 text-amber-800';
      case 'non_actif': return 'bg-zinc-200 text-zinc-700';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Filtrer les membres par catégorie
  const getCompletMembers = () => {
    return members.filter(m => !m.has_missing_data && !m.has_missing_email && m.email);
  };

  const getMissingEmailMembers = () => {
    return members.filter(m => m.has_missing_email && !m.has_missing_data);
  };

  const getIncompleteMembers = () => {
    return members.filter(m => m.has_missing_data);
  };

  // Filtrer avec recherche par catégorie
  const filterMembersByCategory = (membersList, searchTerm) => {
    if (!searchTerm) return membersList;
    
    return membersList.filter(member => 
      member.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.email && member.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const getFilteredCompletMembers = () => {
    return filterMembersByCategory(getCompletMembers(), searchTermComplets);
  };

  const getFilteredMissingEmailMembers = () => {
    return filterMembersByCategory(getMissingEmailMembers(), searchTermManquants);
  };

  const getFilteredIncompleteMembers = () => {
    return filterMembersByCategory(getIncompleteMembers(), searchTermIncomplets);
  };

  const handleToggleActive = async (memberId, currentStatus) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${memberId}/toggle-active`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await fetchMembers();
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleToggleModerator = async (memberId) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${memberId}/toggle-moderator`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await fetchMembers();
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) {
      try {
        const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${memberId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          await fetchMembers();
        }
      } catch (error) {
        console.error('Erreur:', error);
      }
    }
  };

  const openModal = (type, member = null) => {
    setModalType(type);
    setSelectedMember(member);
    setShowModal(true);
  };

  const handleImportExcel = async () => {
    if (!importFile) return;
    
    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/import-members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setImportResult(result);
        await fetchMembers(); // Rafraîchir la liste
      } else {
        setImportResult({ 
          success: false, 
          message: result.detail || 'Erreur lors de l\'import' 
        });
      }
    } catch (error) {
      console.error('Erreur:', error);
      setImportResult({ 
        success: false, 
        message: 'Erreur réseau lors de l\'import' 
      });
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportResult(null);
    setShowImportModal(false);
  };

  // Fonction pour renouveler le token si nécessaire
  const refreshTokenIfNeeded = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      if (currentUser.email) {
        // Simuler une reconnexion automatique
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: currentUser.email,
            password: localStorage.getItem('tempPassword') || 'admin123' // À améliorer en production
          })
        });

        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('adminToken', data.access_token);
          console.log('✅ Token renouvelé automatiquement');
          return data.access_token;
        }
      }
    } catch (error) {
      console.error('Erreur renouvellement token:', error);
    }
    return null;
  };

  const downloadTemplate = async () => {
    try {
      let token = localStorage.getItem('adminToken');
      
      if (!token) {
        alert('Erreur: Token d\'authentification manquant. Veuillez vous reconnecter.');
        return;
      }

      console.log('Téléchargement template depuis:', process.env.REACT_APP_BACKEND_URL);
      
      let response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/download-template`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      // Si erreur 401, rediriger vers logout
      if (response.status === 401 || response.status === 403) {
        console.warn('Session expirée - Redirection vers logout');
        window.location.href = '/logout';
        return;
      }

      console.log('Réponse téléchargement:', response.status, response.statusText);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Template_Import_Membres_Lignages.xlsx';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        console.log('✅ Template téléchargé avec succès');
      } else {
        const errorText = await response.text();
        console.error('Erreur téléchargement:', response.status, errorText);
        alert(`Erreur lors du téléchargement du template: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur lors du téléchargement du template: ${error.message}`);
    }
  };

  const exportMembers = async () => {
    try {
      let token = localStorage.getItem('adminToken');
      
      if (!token) {
        alert('Erreur: Token d\'authentification manquant. Veuillez vous reconnecter.');
        return;
      }

      console.log('Export membres depuis:', process.env.REACT_APP_BACKEND_URL);
      
      let response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export-members`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      // Si erreur 401, rediriger vers logout
      if (response.status === 401 || response.status === 403) {
        console.warn('Session expirée - Redirection vers logout');
        window.location.href = '/logout';
        return;
      }

      console.log('Réponse export:', response.status, response.statusText);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Extraire le nom de fichier depuis les headers si disponible
        const contentDisposition = response.headers.get('content-disposition');
        let filename = 'Export_Membres_Lignages.xlsx';
        if (contentDisposition) {
          const filenamePart = contentDisposition.split('filename=')[1];
          if (filenamePart) {
            filename = filenamePart.replace(/"/g, '');
          }
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        console.log('✅ Export réussi:', filename);
      } else {
        const errorText = await response.text();
        console.error('Erreur export:', response.status, errorText);
        alert(`Erreur lors de l'export des membres: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur lors de l'export des membres: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Chargement des membres...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header responsive */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <h1 
          className="text-2xl md:text-3xl font-bold" 
          style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
        >
          Gestion des membres
        </h1>
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={() => {
              const printWindow = window.open('', '_blank', 'width=800,height=900');
              printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                  <title>Lettre de connexion - Les Lignages de Bruxelles</title>
                  <style>
                    body {
                      font-family: 'Georgia', serif;
                      padding: 40px 60px;
                      color: #333;
                      line-height: 1.6;
                      max-width: 800px;
                      margin: 0 auto;
                    }
                    .header {
                      text-align: center;
                      margin-bottom: 60px;
                      border-bottom: 2px solid rgb(196, 150, 96);
                      padding-bottom: 15px;
                    }
                    .logo {
                      max-width: 100px;
                      margin-bottom: 12px;
                    }
                    h1 {
                      color: rgb(196, 150, 96);
                      font-size: 20px;
                      margin: 5px 0;
                      font-weight: normal;
                    }
                    h2 {
                      color: rgb(196, 150, 96);
                      font-size: 16px;
                      margin-top: 20px;
                      margin-bottom: 10px;
                    }
                    p {
                      margin-bottom: 12px;
                      text-align: justify;
                    }
                    .greeting {
                      margin-top: 0;
                      margin-bottom: 35px;
                    }
                    .intro {
                      margin-bottom: 30px;
                    }
                    .divider {
                      border-top: 1px solid #ddd;
                      margin: 20px 0;
                    }
                    .page-break {
                      page-break-before: always;
                      margin-top: 0;
                      padding-top: 40px;
                    }
                    .steps {
                      margin-left: 30px;
                      margin-bottom: 15px;
                    }
                    .steps li {
                      margin-bottom: 6px;
                    }
                    ul {
                      margin-left: 30px;
                      margin-bottom: 15px;
                    }
                    ul li {
                      margin-bottom: 6px;
                    }
                    .cta {
                      text-align: center;
                      margin: 20px 0;
                      padding: 15px;
                      background-color: #f9fafb;
                      border-radius: 8px;
                      border: 2px solid rgb(196, 150, 96);
                    }
                    .cta a {
                      color: rgb(196, 150, 96);
                      text-decoration: none;
                      font-weight: bold;
                      font-size: 16px;
                    }
                    .footer {
                      margin-top: 30px;
                      padding-top: 20px;
                      border-top: 2px solid rgb(196, 150, 96);
                    }
                    .signature {
                      margin-top: 25px;
                      font-style: italic;
                    }
                    .contact {
                      margin-top: 15px;
                      font-size: 13px;
                      color: #666;
                    }
                    @media print {
                      body {
                        padding: 40px;
                      }
                    }
                  </style>
                </head>
                <body>
                  <div class="header">
                    <img src="${process.env.REACT_APP_BACKEND_URL}/images/lignages-logo-admin.png" alt="Logo" class="logo">
                    <h1>Les Lignages de Bruxelles</h1>
                  </div>

                  <div class="greeting">
                    <p>Chère Madame, Cher Monsieur,<br>
                    Chers membres des Lignages de Bruxelles,</p>
                  </div>

                  <p class="intro">
                    Nous avons le plaisir de vous annoncer la mise en ligne du nouveau site des Lignages de Bruxelles.
                    Chaque membre dispose désormais d'un compte personnel lui permettant d'accéder à toutes les informations et services réservés aux lignagers.
                  </p>

                  <div class="divider"></div>

                  <h2>🔑 Première connexion : une étape simple et sécurisée</h2>

                  <p>Pour des raisons de sécurité, vos anciens identifiants et mots de passe ne sont plus valables.</p>

                  <p>La première fois que vous vous connecterez, veuillez utiliser la connexion simplifiée :</p>

                  <ol class="steps">
                    <li>Rendez-vous sur le site : <strong>https://lignagesdebruxelles.be</strong></li>
                    <li>Cliquez sur « <strong>Connexion simplifiée</strong> »</li>
                    <li>Indiquez votre adresse e-mail (celle à laquelle vous recevez ce message)</li>
                    <li>Vous recevrez automatiquement un courriel contenant un lien personnel et sécurisé, valable 12 heures</li>
                    <li>En cliquant sur ce lien, vous accéderez directement à votre espace membre</li>
                    <li>Vous pourrez ensuite définir un nouveau mot de passe pour vos connexions futures</li>
                  </ol>

                  <p>Une fois ce mot de passe créé, vous pourrez ensuite vous connecter de manière classique, avec votre adresse e-mail et votre nouveau mot de passe.</p>

                  <h2 class="page-break">💳 Nouveau : paiement en ligne simplifié avec Stripe</h2>

                  <p>
                    Le nouveau site intègre désormais le système de paiement sécurisé <strong>Stripe</strong>,
                    vous permettant de régler vos cotisations et vos participations aux événements
                    en toute simplicité, directement depuis votre espace membre.
                    Le paiement est rapide, sécurisé et accessible depuis n'importe quel appareil.
                  </p>

                  <div class="divider"></div>

                  <h2>🌟 Dans votre espace « Membres », vous pouvez :</h2>

                  <ul>
                    <li>Gérer votre cotisation annuelle et vos inscriptions,</li>
                    <li>Mettre à jour vos informations personnelles et familiales,</li>
                    <li>Consulter les actualités et publications des Lignages,</li>
                    <li>Découvrir les événements à venir et vous y inscrire,</li>
                    <li>Participer au forum des membres,</li>
                    <li>Explorer les familles et leurs blasons.</li>
                  </ul>

                  <div class="divider"></div>

                  <p>
                    Nous vous invitons à vous connecter dès aujourd'hui et à découvrir ce nouvel espace,
                    pensé pour renforcer les liens entre lignagers et valoriser notre patrimoine commun.
                  </p>

                  <div class="cta">
                    <a href="https://lignagesdebruxelles.be" target="_blank">
                      👉 Accéder au site des Lignages de Bruxelles
                    </a>
                  </div>

                  <div class="footer">
                    <div class="signature">
                      <p>Avec nos salutations distinguées,</p>
                      <p><strong>Le Secrétariat des Lignages de Bruxelles</strong></p>
                    </div>
                    
                    <div class="contact">
                      <p>✉️ contact@lignagesdebruxelles.be</p>
                      <p>🌐 https://lignagesdebruxelles.be</p>
                    </div>
                  </div>

                  <script>
                    window.onload = function() {
                      window.print();
                    };
                    
                    window.onafterprint = function() {
                      window.close();
                    };
                    
                    if (window.matchMedia) {
                      const mediaQueryList = window.matchMedia('print');
                      mediaQueryList.addListener(function(mql) {
                        if (!mql.matches) {
                          window.close();
                        }
                      });
                    }
                  </script>
                </body>
                </html>
              `);
              printWindow.document.close();
            }}
            variant="outline"
            className="border-2"
            style={{ borderColor: 'rgb(196, 150, 96)', color: 'rgb(196, 150, 96)' }}
          >
            <FileDown className="w-4 h-4 mr-2" />
            Template Lettre de connexion
          </Button>
          <Button 
            onClick={() => openModal('create')}
            className="text-white"
            style={{ backgroundColor: 'rgb(196, 150, 96)' }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau membre
          </Button>
        </div>
      </div>

      {/* Statistiques - Comptes */}
      <div className="grid md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold">{members.length}</p>
              <p className="text-gray-600">Total membres</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <User className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-green-600">
                {filteredMembers.filter(m => !m.has_missing_data && !m.has_missing_email && m.email).length}
              </p>
              <p className="text-gray-600">Comptes complets</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-orange-600">
                {filteredMembers.filter(m => m.has_missing_email && !m.has_missing_data).length}
              </p>
              <p className="text-gray-600">Email manquant</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-red-600">
                {filteredMembers.filter(m => m.has_missing_data).length}
              </p>
              <p className="text-gray-600">Données incomplètes</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques - Lignages */}
      <div className="bg-white rounded-lg p-6 shadow-sm border mb-8">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'rgb(196, 150, 96)' }}>
          Répartition par Lignage
        </h3>
        <div className="grid md:grid-cols-7 gap-4">
          {['Coudenberg', 'Roodenbeke', 'Sleeus', 'Steenwegh', 'Serroelofs', 'Serhuyghs', 'Sweerts'].map(lignage => (
            <div key={lignage} className="text-center">
              <div className="text-2xl font-bold" style={{ color: 'rgb(196, 150, 96)' }}>
                {filteredMembers.filter(m => {
                  if (Array.isArray(m.lignage)) {
                    return m.lignage.includes(lignage);
                  }
                  return m.lignage === lignage;
                }).length}
              </div>
              <div className="text-sm text-gray-600">{lignage}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 text-center">
          <div className="text-lg font-semibold text-gray-500">
            {filteredMembers.filter(m => {
              if (Array.isArray(m.lignage)) {
                return m.lignage.length === 0;
              }
              return !m.lignage || m.lignage === '';
            }).length} membres sans lignage spécifié
          </div>
        </div>
      </div>

      {/* Nouveau système d'onglets par catégorie */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger 
            value="complets" 
            className="text-sm data-[state=active]:bg-cream-100 data-[state=active]:text-cream-800"
          >
            Comptes complets ({getCompletMembers().length})
          </TabsTrigger>
          <TabsTrigger 
            value="email-manquant"
            className="text-sm data-[state=active]:bg-orange-100 data-[state=active]:text-orange-800"
          >
            Email manquant ({getMissingEmailMembers().length})
          </TabsTrigger>
          <TabsTrigger 
            value="donnees-incompletes"
            className="text-sm data-[state=active]:bg-red-100 data-[state=active]:text-red-800"
          >
            Données incomplètes ({getIncompleteMembers().length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="complets" className="mt-4">
          <MembersList 
            members={getFilteredCompletMembers()}
            searchTerm={searchTermComplets}
            setSearchTerm={setSearchTermComplets}
            title="Comptes complets"
            emptyMessage="Aucun compte complet trouvé"
            onView={(member) => {
              setSelectedMember(member);
              setModalType('view');
              setShowModal(true);
            }}
            onEdit={(member) => {
              setSelectedMember(member);
              setModalType('edit');
              setShowModal(true);
            }}
            onDelete={handleDeleteMember}
            getRoleBadgeColor={getRoleBadgeColor}
            getRoleLabel={getRoleLabel}
          />
        </TabsContent>

        <TabsContent value="email-manquant" className="mt-4">
          <MembersList 
            members={getFilteredMissingEmailMembers()}
            searchTerm={searchTermManquants}
            setSearchTerm={setSearchTermManquants}
            title="Emails manquants"
            emptyMessage="Aucun membre avec email manquant trouvé"
            onView={(member) => {
              setSelectedMember(member);
              setModalType('view');
              setShowModal(true);
            }}
            onEdit={(member) => {
              setSelectedMember(member);
              setModalType('edit');
              setShowModal(true);
            }}
            onDelete={handleDeleteMember}
            getRoleBadgeColor={getRoleBadgeColor}
            getRoleLabel={getRoleLabel}
          />
        </TabsContent>

        <TabsContent value="donnees-incompletes" className="mt-4">
          <MembersList 
            members={getFilteredIncompleteMembers()}
            searchTerm={searchTermIncomplets}
            setSearchTerm={setSearchTermIncomplets}
            title="Données incomplètes"
            emptyMessage="Aucun membre avec données incomplètes trouvé"
            onView={(member) => {
              setSelectedMember(member);
              setModalType('view');
              setShowModal(true);
            }}
            onEdit={(member) => {
              setSelectedMember(member);
              setModalType('edit');
              setShowModal(true);
            }}
            onDelete={handleDeleteMember}
            getRoleBadgeColor={getRoleBadgeColor}
            getRoleLabel={getRoleLabel}
          />
        </TabsContent>
      </Tabs>

      {/* Modal pour afficher/éditer membre */}
      {showModal && (
        <MemberModal 
          member={selectedMember}
          type={modalType}
          onClose={() => setShowModal(false)}
          onRefresh={fetchMembers}
        />
      )}

      {/* Modal pour import Excel */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 
                className="text-2xl font-bold"
                style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
              >
                Import des membres Excel
              </h2>
              <Button variant="outline" onClick={resetImport}>
                ✕
              </Button>
            </div>

            {!importResult ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sélectionnez le fichier Excel (.xlsx)
                  </label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Format accepté: .xlsx, .xls
                  </p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-3">
                  <div className="flex">
                    <div className="ml-3">
                      <p className="text-sm text-blue-800">
                        <strong>Nouveau format Excel:</strong><br/>
                        • <strong>Obligatoire:</strong> Prénom, Nom<br/>
                        • <strong>Optionnel:</strong> Email, Téléphone, Adresse, etc.<br/>
                        • <strong>Type_Membre:</strong> "Vie", "Annuel", "Extérieur"<br/>
                        • <strong>Langue:</strong> "FR" ou "NL"<br/>
                        • Téléchargez le template pour voir la structure complète
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-2 mt-6">
                  <Button variant="outline" onClick={resetImport}>
                    Annuler
                  </Button>
                  <Button 
                    onClick={handleImportExcel}
                    disabled={!importFile || importing}
                    className="text-white"
                    style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                  >
                    {importing ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Import...
                      </div>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Importer
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${
                  importResult.success === false ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                } border`}>
                  <h3 className="font-semibold mb-2">
                    {importResult.success === false ? 'Erreur d\'import' : 'Import terminé'}
                  </h3>
                  
                  {importResult.success === false ? (
                    <p className="text-red-700">{importResult.message}</p>
                  ) : (
                    <div className="text-green-700">
                      <p><strong>Membres importés:</strong> {importResult.imported_count}</p>
                      <p><strong>Membres ignorés (doublons):</strong> {importResult.skipped_count}</p>
                      {importResult.incomplete_count > 0 && (
                        <p><strong>Comptes incomplets:</strong> {importResult.incomplete_count}</p>
                      )}
                      <p><strong>Total traité:</strong> {importResult.total_processed}</p>
                      
                      {importResult.errors && importResult.errors.length > 0 && (
                        <div className="mt-2">
                          <p><strong>Premières erreurs ({importResult.errors.length} affichées):</strong></p>
                          <div className="max-h-32 overflow-y-auto bg-red-50 p-2 rounded text-xs">
                            <ul className="list-disc list-inside">
                              {importResult.errors.slice(0, 5).map((error, index) => (
                                <li key={index} className="text-red-600 mb-1">{error}</li>
                              ))}
                              {importResult.errors.length > 5 && (
                                <li className="text-red-800 font-bold">... et {importResult.errors.length - 5} autres erreurs</li>
                              )}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end">
                  <Button 
                    onClick={resetImport}
                    className="text-white"
                    style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                  >
                    Fermer
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Composant Modal pour gérer un membre
const MemberModal = ({ member, type, onClose, onRefresh }) => {
  const [formData, setFormData] = useState({
    first_name: member?.first_name || '',
    last_name: member?.last_name || '',
    email: member?.email || '',
    phone: member?.phone || '',
    gsm: member?.gsm || '',
    date_naissance: member?.date_naissance || '',
    address: member?.address || '',
    city: member?.city || '',
    postal_code: member?.postal_code || '',
    country: member?.country || '',
    role: member?.role || 'membre_exterieur',
    language: member?.language || '',
    civilite: member?.civilite || '',
    is_moderator: member?.is_moderator || false,
    cotisation_payee: member?.cotisation_payee || false,
    date_paiement: member?.date_paiement || '',
    annee_cotisation: member?.annee_cotisation || new Date().getFullYear().toString(),
    montant_cotisation: member?.montant_cotisation || '',
    mode_paiement: member?.mode_paiement || '',
    statut_cotisation: member?.statut_cotisation || 'En_retard',
    lignage: Array.isArray(member?.lignage) ? member.lignage : [],
    rgpd_consent: member?.rgpd_consent || false,
    profile_picture: member?.profile_picture || '',
    blason_familial: member?.blason_familial || '',
    admin_notes: member?.admin_notes || '',
    password: member?.password || ''
  });
  
  // États pour la gestion des mots de passe
  const [showPassword, setShowPassword] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  
  // États pour l'upload d'images
  const [profileImageUploading, setProfileImageUploading] = useState(false);
  const [blasonImageUploading, setBlasonImageUploading] = useState(false);
  const [profileImagePreview, setProfileImagePreview] = useState(member?.profile_picture || '');
  const [blasonImagePreview, setBlasonImagePreview] = useState(member?.blason_familial || '');
  
  // Fonction pour copier le mot de passe dans le presse-papiers
  const copyPassword = async () => {
    if (formData.password) {
      try {
        await navigator.clipboard.writeText(formData.password);
        alert('Mot de passe copié dans le presse-papiers !');
      } catch (error) {
        alert(`Mot de passe: ${formData.password}`);
      }
    }
  };
  
  // Fonction pour générer un nouveau mot de passe
  const generateNewPassword = () => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%&*+=-?';
    
    const allChars = lowercase + uppercase + numbers + symbols;
    let password = '';
    
    // Assurer au moins un caractère de chaque type
    password += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
    password += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));
    password += symbols.charAt(Math.floor(Math.random() * symbols.length));
    
    // Remplir le reste
    for (let i = 4; i < 12; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }
    
    // Mélanger le mot de passe
    const shuffled = password.split('').sort(() => 0.5 - Math.random()).join('');
    
    setFormData({...formData, password: shuffled});
    setEditingPassword(true);
  };
  
  // Fonction pour uploader une image
  const uploadImage = async (file, type) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      if (type === 'profile') {
        setProfileImageUploading(true);
      } else {
        setBlasonImageUploading(true);
      }
      
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataUpload,
      });
      
      if (response.ok) {
        const result = await response.json();
        
        if (type === 'profile') {
          setFormData({...formData, profile_picture: result.file_url});
          setProfileImagePreview(result.file_url);
        } else {
          setFormData({...formData, blason_familial: result.file_url});
          setBlasonImagePreview(result.file_url);
        }
      } else {
        alert('Erreur lors de l\'upload de l\'image');
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors de l\'upload de l\'image');
    } finally {
      if (type === 'profile') {
        setProfileImageUploading(false);
      } else {
        setBlasonImageUploading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const url = type === 'create' 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/users`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${member.id}`;
      
      const method = type === 'create' ? 'POST' : 'PUT';
      
      // Préparer le body selon le type d'action
      let body = { ...formData };
      
      if (type === 'edit') {
        // Pour la modification, inclure le mot de passe seulement s'il a été modifié
        if (formData.password === member?.password && !editingPassword) {
          // Enlever le mot de passe si il n'a pas été modifié
          const { password, ...bodyWithoutPassword } = formData;
          body = bodyWithoutPassword;
        }
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        onRefresh();
        onClose();
      } else {
        const errorData = await response.json();
        console.error('Erreur lors de la sauvegarde:', errorData);
        alert(`Erreur: ${errorData.detail || 'Erreur lors de la sauvegarde'}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur réseau lors de la sauvegarde');
    }
  };

  const isReadonly = type === 'view';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 
            className="text-2xl font-bold"
            style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
          >
            {type === 'create' ? 'Nouveau membre' : 
             type === 'edit' ? 'Modifier membre' : 'Détails du membre'}
          </h2>
          <Button variant="outline" onClick={onClose}>
            ✕
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                disabled={isReadonly}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                disabled={isReadonly}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              disabled={isReadonly}
              required
            />
          </div>

          {/* Section Mot de passe - toujours affichée sauf en mode création */}
          {type !== 'create' && formData.password && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
              <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    disabled={isReadonly || !editingPassword}
                    className="pr-20"
                  />
                  {!isReadonly && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="p-1 text-gray-500 hover:text-gray-700"
                        title={showPassword ? "Masquer" : "Afficher"}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={copyPassword}
                        className="p-1 text-green-600 hover:text-green-700"
                        title="Copier le mot de passe"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                
                {!isReadonly && (
                  <div className="flex space-x-1">
                    {!editingPassword ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingPassword(true)}
                        className="whitespace-nowrap"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Modifier
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingPassword(false)}
                        className="whitespace-nowrap"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        OK
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={generateNewPassword}
                      className="whitespace-nowrap"
                      title="Générer un nouveau mot de passe"
                    >
                      <Key className="w-4 h-4 mr-1" />
                      Générer
                    </Button>
                  </div>
                )}
              </div>
              {editingPassword && (
                <p className="text-xs text-blue-600 mt-1">
                  Vous êtes en mode édition. Cliquez sur "OK" pour sauvegarder les modifications.
                </p>
              )}
            </div>
          )}

          {type === 'create' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
              <div className="flex items-center space-x-2">
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateNewPassword}
                  className="whitespace-nowrap"
                  title="Générer un mot de passe sécurisé"
                >
                  <Key className="w-4 h-4 mr-1" />
                  Générer
                </Button>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                disabled={isReadonly}
                placeholder="ex: +32 2 123 45 67"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">GSM</label>
              <Input
                value={formData.gsm}
                onChange={(e) => setFormData({...formData, gsm: e.target.value})}
                disabled={isReadonly}
                placeholder="ex: +32 485 12 34 56"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              disabled={isReadonly}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                disabled={isReadonly}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
              <Input
                value={formData.postal_code}
                onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                disabled={isReadonly}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
              <Input
                type="date"
                value={formData.date_naissance}
                onChange={(e) => setFormData({...formData, date_naissance: e.target.value})}
                disabled={isReadonly}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pays</label>
              <Input
                value={formData.country}
                onChange={(e) => setFormData({...formData, country: e.target.value})}
                disabled={isReadonly}
                placeholder="Belgique"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Langue</label>
              <select 
                value={formData.language} 
                onChange={(e) => setFormData({...formData, language: e.target.value})}
                disabled={isReadonly}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Non spécifié</option>
                <option value="FR">Français (FR)</option>
                <option value="NL">Néerlandais (NL)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Civilité</label>
              <Input
                value={formData.civilite}
                onChange={(e) => setFormData({...formData, civilite: e.target.value})}
                disabled={isReadonly}
                placeholder="ex: Monsieur, Madame, De Heer..."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
            <select 
              value={formData.role} 
              onChange={(e) => setFormData({...formData, role: e.target.value})}
              disabled={isReadonly}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="membre_exterieur">Membre extérieur</option>
              <option value="membre_annuel">Membre annuel</option>
              <option value="membre_vie">Membre à vie</option>
              <option value="admin">Administrateur</option>
              <option value="prospect">Prospect</option>
              <option value="non_actif">Non-actif</option>
              <option value="decede">Décédé</option>
            </select>
          </div>

          {/* Section Forum */}
          <div className="border-t pt-4 mt-4">
            <h4 className="text-lg font-semibold mb-3" style={{ color: 'rgb(196, 150, 96)' }}>
              Forum
            </h4>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_moderator"
                checked={formData.is_moderator || false}
                onChange={(e) => setFormData({...formData, is_moderator: e.target.checked})}
                disabled={isReadonly}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_moderator" className="ml-2 text-sm font-medium text-gray-700">
                Nommer l'utilisateur comme modérateur
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2 ml-6">
              Les modérateurs peuvent supprimer des sujets, supprimer des réponses, modifier les contenus du forum et accéder au forum modérateur.
            </p>
          </div>

          {/* Section Cotisations */}
          <div className="border-t pt-4 mt-4">
            <h4 className="text-lg font-semibold mb-3" style={{ color: 'rgb(196, 150, 96)' }}>
              Informations de cotisation
            </h4>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cotisation payée</label>
                <select 
                  value={formData.cotisation_payee ? 'true' : 'false'} 
                  onChange={(e) => setFormData({...formData, cotisation_payee: e.target.value === 'true'})}
                  disabled={isReadonly}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="false">Non</option>
                  <option value="true">Oui</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Année cotisation</label>
                <Input
                  value={formData.annee_cotisation}
                  onChange={(e) => setFormData({...formData, annee_cotisation: e.target.value})}
                  disabled={isReadonly}
                  placeholder="2024"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de paiement</label>
                <Input
                  type="date"
                  value={formData.date_paiement}
                  onChange={(e) => setFormData({...formData, date_paiement: e.target.value})}
                  disabled={isReadonly}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Montant (€)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.montant_cotisation}
                  onChange={(e) => setFormData({...formData, montant_cotisation: e.target.value})}
                  disabled={isReadonly}
                  placeholder="50.00"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
                <select 
                  value={formData.mode_paiement} 
                  onChange={(e) => setFormData({...formData, mode_paiement: e.target.value})}
                  disabled={isReadonly}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">Non spécifié</option>
                  <option value="Virement">Virement</option>
                  <option value="Chèque">Chèque</option>
                  <option value="Espèces">Espèces</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut cotisation</label>
                <select 
                  value={formData.statut_cotisation} 
                  onChange={(e) => setFormData({...formData, statut_cotisation: e.target.value})}
                  disabled={isReadonly}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="En_retard">En retard</option>
                  <option value="En_ordre">En ordre</option>
                  <option value="Exemption">Exemption</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section RGPD */}
          <div className="border-t pt-4 mt-4">
            <h4 className="text-lg font-semibold mb-3" style={{ color: 'rgb(196, 150, 96)' }}>
              Consentement RGPD
            </h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Accepte de recevoir des communications
              </label>
              <select 
                value={formData.rgpd_consent ? 'true' : 'false'} 
                onChange={(e) => setFormData({...formData, rgpd_consent: e.target.value === 'true'})}
                disabled={isReadonly}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="false">Non - Pas de communications</option>
                <option value="true">Oui - Accepte les communications</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Si "Non", le membre ne recevra pas d'emails ou d'appels
              </p>
            </div>
          </div>

          {/* Section Photos, Blasons et Lignage */}
          <div className="border-t pt-4 mt-4">
            <h4 className="text-lg font-semibold mb-3" style={{ color: 'rgb(196, 150, 96)' }}>
              Photos, Blasons et Lignage
            </h4>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Photo de profil */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Photo de profil
                </label>
                
                {/* Prévisualisation de l'image */}
                {profileImagePreview && (
                  <div className="mb-3 relative">
                    <img 
                      src={profileImagePreview} 
                      alt="Photo de profil" 
                      className="w-24 h-24 object-cover rounded-full border border-gray-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        setProfileImagePreview('');
                      }}
                    />
                    {!isReadonly && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({...formData, profile_picture: ''});
                          setProfileImagePreview('');
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                        title="Supprimer l'image"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}
                
                {/* URL manuelle - MASQUÉ pour simplifier l'interface */}
                {/* <Input
                  value={formData.profile_picture}
                  onChange={(e) => {
                    setFormData({...formData, profile_picture: e.target.value});
                    setProfileImagePreview(e.target.value);
                  }}
                  disabled={isReadonly}
                  placeholder="URL de la photo de profil"
                  className="mb-2"
                /> */}
                
                {/* Upload de fichier */}
                {!isReadonly && (
                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          uploadImage(e.target.files[0], 'profile');
                        }
                      }}
                      disabled={profileImageUploading}
                      className="hidden"
                      id="profile-image-upload"
                    />
                    <label
                      htmlFor="profile-image-upload"
                      className={`cursor-pointer flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 ${
                        profileImageUploading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {profileImageUploading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                          Upload...
                        </>
                      ) : (
                        <>
                          <Image className="w-4 h-4 mr-2" />
                          Choisir une image
                        </>
                      )}
                    </label>
                    <span className="text-xs text-gray-500">JPG, PNG, WEBP</span>
                  </div>
                )}
              </div>
              
              {/* Blason familial */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Blason familial
                </label>
                
                {/* Prévisualisation de l'image */}
                {blasonImagePreview && (
                  <div className="mb-3 relative">
                    <img 
                      src={blasonImagePreview} 
                      alt="Blason familial" 
                      className="w-24 h-24 object-cover rounded border border-gray-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        setBlasonImagePreview('');
                      }}
                    />
                    {!isReadonly && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({...formData, blason_familial: ''});
                          setBlasonImagePreview('');
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                        title="Supprimer l'image"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}
                
                {/* URL manuelle - MASQUÉ pour simplifier l'interface */}
                {/* <Input
                  value={formData.blason_familial}
                  onChange={(e) => {
                    setFormData({...formData, blason_familial: e.target.value});
                    setBlasonImagePreview(e.target.value);
                  }}
                  disabled={isReadonly}
                  placeholder="URL du blason familial"
                  className="mb-2"
                /> */}
                
                {/* Upload de fichier */}
                {!isReadonly && (
                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          uploadImage(e.target.files[0], 'blason');
                        }
                      }}
                      disabled={blasonImageUploading}
                      className="hidden"
                      id="blason-image-upload"
                    />
                    <label
                      htmlFor="blason-image-upload"
                      className={`cursor-pointer flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 ${
                        blasonImageUploading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {blasonImageUploading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                          Upload...
                        </>
                      ) : (
                        <>
                          <Image className="w-4 h-4 mr-2" />
                          Choisir une image
                        </>
                      )}
                    </label>
                    <span className="text-xs text-gray-500">JPG, PNG, WEBP</span>
                  </div>
                )}
              </div>
            </div>

            {/* Lignage - maintenant dans la même section avec sélection multiple */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Lignages (sélection multiple)</label>
              <div className="border border-gray-300 rounded-md p-3 bg-gray-50">
                {['Coudenberg', 'Roodenbeke', 'Sleeus', 'Steenwegh', 'Serroelofs', 'Serhuyghs', 'Sweerts'].map((lignageName) => (
                  <label key={lignageName} className="flex items-center py-2 cursor-pointer hover:bg-gray-100 px-2 rounded">
                    <input
                      type="checkbox"
                      checked={Array.isArray(formData.lignage) ? formData.lignage.includes(lignageName) : formData.lignage === lignageName}
                      onChange={(e) => {
                        const currentLignages = Array.isArray(formData.lignage) ? formData.lignage : (formData.lignage ? [formData.lignage] : []);
                        if (e.target.checked) {
                          setFormData({...formData, lignage: [...currentLignages, lignageName]});
                        } else {
                          setFormData({...formData, lignage: currentLignages.filter(l => l !== lignageName)});
                        }
                      }}
                      disabled={isReadonly}
                      className="mr-3 h-4 w-4"
                      style={{ accentColor: 'rgb(196, 150, 96)' }}
                    />
                    <span className="text-sm">{lignageName}</span>
                  </label>
                ))}
                {!isReadonly && formData.lignage && Array.isArray(formData.lignage) && formData.lignage.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, lignage: []})}
                    className="mt-2 text-xs text-red-600 hover:text-red-800"
                  >
                    Désélectionner tout
                  </button>
                )}
              </div>
              {formData.lignage && Array.isArray(formData.lignage) && formData.lignage.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {formData.lignage.length} lignage{formData.lignage.length > 1 ? 's' : ''} sélectionné{formData.lignage.length > 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>

          {/* Section Notes Administrateur (séparée) */}
          <div className="border-t pt-4 mt-4">
            <h4 className="text-lg font-semibold mb-3" style={{ color: 'rgb(196, 150, 96)' }}>
              Notes Administrateur
            </h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes Administrateur (confidentielles)
              </label>
              <textarea
                value={formData.admin_notes}
                onChange={(e) => setFormData({...formData, admin_notes: e.target.value})}
                disabled={isReadonly}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Notes privées de l'administrateur..."
              />
            </div>
          </div>

          {!isReadonly && (
            <div className="flex justify-end space-x-2 mt-6">
              <Button type="button" variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button 
                type="submit"
                className="text-white"
                style={{ backgroundColor: 'rgb(196, 150, 96)' }}
              >
                {type === 'create' ? 'Créer' : 'Sauvegarder'}
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default MembersManagement;